/**
 * The data-tables REST surface speaks snake_case (its DTO contract), but the
 * dashboard types (`types.ts`) are camelCase throughout, and the shared
 * `apiRequest` helper does no transform. So the boundary is broken BOTH ways:
 *  - Responses: snake fields (`schema_table`, `next_cursor`, …) read back as
 *    `undefined` → use {@link camelizeKeys} / {@link camelizeEnvelope}.
 *  - Request bodies: camel fields (`sqlTemplate`, `isIndexed`, …) are rejected
 *    or silently dropped by the API → use {@link snakeizeKeys}.
 *
 * Two traps these must respect, so they never corrupt user data:
 *  - **Opaque blobs**: fields holding user-keyed data whose keys must NOT be
 *    rewritten — `params`/`args` (query args), `param_map`/`paramMap` (binding
 *    mappings), `credentials` (connector secrets), and row `values`. Their
 *    contents pass through verbatim.
 *  - **Tenant rows**: `DataRow` values are keyed by the user's own column names
 *    (which may be snake_case, e.g. `first_name`). Row-bearing responses use
 *    {@link camelizeEnvelope} (top-level keys only) so row contents stay raw;
 *    bare rows are not transformed at all.
 */

/** Response keys whose values are opaque user data — don't recurse into them. */
const OPAQUE_KEYS = new Set(["params", "param_map"]);

/** Request keys whose values are opaque user data — don't recurse into them. */
const OPAQUE_REQUEST_KEYS = new Set(["params", "args", "paramMap", "credentials", "values"]);

function snakeToCamel(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_match, c: string) => c.toUpperCase());
}

function camelToSnake(key: string): string {
  return key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

function transform(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(transform);
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      out[snakeToCamel(key)] = OPAQUE_KEYS.has(key) ? val : transform(val);
    }
    return out;
  }
  return value;
}

/** Deep snake→camel for metadata responses (objects, arrays, nested). */
export function camelizeKeys<T = unknown>(value: unknown): T {
  return transform(value) as T;
}

function snakeify(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(snakeify);
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      out[camelToSnake(key)] = OPAQUE_REQUEST_KEYS.has(key) ? val : snakeify(val);
    }
    return out;
  }
  return value;
}

/**
 * Deep camel→snake for request bodies. Opaque blobs (`args`, `paramMap`,
 * `credentials`, row `values`) pass through with their keys untouched.
 */
export function snakeizeKeys<T = unknown>(value: unknown): T {
  return snakeify(value) as T;
}

/**
 * Shallow snake→camel: converts only the top-level keys and leaves every value
 * verbatim. Use for row-bearing responses (`Paged<DataRow>`, query/preview
 * results) so the envelope (`next_cursor`, `row_count`, …) is camelized while
 * the opaque tenant rows keep their original column-name keys.
 */
export function camelizeEnvelope<T = unknown>(value: unknown): T {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return value as T;
  }
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value)) {
    out[snakeToCamel(key)] = val;
  }
  return out as T;
}
